import { useState } from 'react';
import Papa from 'papaparse';
import { supabase } from '../../lib/supabase';
import Button from '../common/Button';
import Card from '../common/Card';

export default function StudentImport({ escolaId, onComplete }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState([]);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            Papa.parse(selectedFile, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    setPreview(results.data.slice(0, 5));
                    setError(null);
                },
                error: (err) => {
                    setError('Erro ao ler arquivo: ' + err.message);
                }
            });
        }
    };

    const handleImport = async () => {
        if (!file || !escolaId) return;

        try {
            setImporting(true);
            setError(null);

            // 1. Carregar Portões existentes para mapeamento
            const { data: currentGates } = await supabase
                .from('portoes')
                .select('*')
                .eq('escola_id', escolaId);

            const gateMap = {};
            currentGates?.forEach(g => {
                gateMap[g.nome.toLowerCase().trim()] = g.id;
            });

            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    const formattedData = results.data.map((row) => {
                        const gateName = (row.portao || row.gate || '').toLowerCase().trim();
                        return {
                            escola_id: escolaId,
                            nome_completo: row.nome || row.aluno || row.nome_completo,
                            turma: row.turma || row.serie || row.classe,
                            observacoes: row.observacoes || '',
                            portao_id: gateMap[gateName] || null
                        };
                    }).filter(item => item.nome_completo);

                    if (formattedData.length === 0) {
                        setError('Nenhum dado válido encontrado. Verifique os cabeçalhos (nome, turma).');
                        setImporting(false);
                        return;
                    }

                    const { error: insertError } = await supabase
                        .from('alunos')
                        .insert(formattedData);

                    if (insertError) throw insertError;

                    setSuccess(true);
                    if (onComplete) onComplete();
                }
            });
        } catch (err) {
            console.error('Erro na importação:', err);
            setError('Erro ao salvar no banco: ' + err.message);
        } finally {
            setImporting(false);
        }
    };

    if (success) {
        return (
            <Card className="text-center py-8">
                <div className="text-5xl mb-4">🎉</div>
                <h3 className="text-xl font-bold text-success mb-2">Importação Concluída!</h3>
                <p className="text-gray-600 mb-6">Os alunos foram cadastrados com sucesso.</p>
                <Button variant="primary" onClick={() => setSuccess(false)}>
                    Importar Mais
                </Button>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            <Card>
                <h3 className="text-lg font-bold mb-4">Importar Alunos via CSV</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Seu arquivo deve conter as colunas: <strong>nome</strong> e <strong>turma</strong>.
                </p>

                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-primary transition-colors cursor-pointer relative">
                    <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="text-4xl mb-2">📄</div>
                    {file ? (
                        <p className="font-medium text-primary">{file.name}</p>
                    ) : (
                        <p className="text-gray-500">Clique ou arraste um arquivo .csv aqui</p>
                    )}
                </div>

                {error && (
                    <div className="mt-4 p-3 bg-red-100 border border-red-300 text-red-800 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {preview.length > 0 && !importing && (
                    <div className="mt-6">
                        <h4 className="text-sm font-semibold mb-2">Prévia dos dados (primeiras 5 linhas):</h4>
                        <p className="text-sm text-gray-600 mb-6">
                            Seu arquivo deve conter as colunas: <strong>nome</strong>, <strong>turma</strong> e opcionalmente <strong>portao</strong>.
                        </p>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b">
                                        <th className="p-2">Nome</th>
                                        <th className="p-2">Turma</th>
                                        <th className="p-2">Portão</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.map((row, i) => (
                                        <tr key={i} className="border-b">
                                            <td className="p-2">{row.nome || row.aluno || row.nome_completo}</td>
                                            <td className="p-2">{row.turma || row.serie || row.classe}</td>
                                            <td className="p-2">{row.portao || row.gate || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <Button
                            variant="primary"
                            className="w-full mt-6"
                            onClick={handleImport}
                            loading={importing}
                        >
                            🚀 Confirmar Importação
                        </Button>
                    </div>
                )}
            </Card>

            <Card className="bg-blue-50 border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2">💡 Dica de Formato</h4>
                <p className="text-sm text-blue-700">
                    Você pode exportar uma planilha do Excel como CSV. Use cabeçalhos simples como "nome" e "turma".
                    Aceitamos variações como "aluno", "nome_completo", "serie" e "classe".
                </p>
            </Card>
        </div>
    );
}
